import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import projectUtils from "../../../../../utils/project-utils.js";
import ShowHideColumnsGrid from "../../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import AssociationRenderer from "./grid-renderers/association-renderer.jsx";
import IndividualPhoto from "./grid-renderers/face-renderer.jsx";
import IndividualGridPagination, {
    getIndividualDefaultPageSize,
    getIndividualPaginatedRowData
} from "./individual-grid-pagination.jsx";

const HIDDEN_COLUMNS_STORAGE = "individuals-hidden-columns";
const ORDER_COLUMNS_STORAGE = "individuals-order-columns";
const individualJsonCache = new WeakMap();

const getStorageJson = (key) => {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
}

const formatBoolean = (value) => {
    if (value === true) return "Sim";
    if (value === false || value === null) return "Não";

    return "";
}

const booleanComparator = (a, b) => Number(a === true) - Number(b === true);

const formatObjectValue = (item) => {
    if (!item) return "";
    if (typeof item === "string" || typeof item === "number") return item.toString();

    const value = item.name
        ?? item.description
        ?? item.text
        ?? item.label
        ?? item.value
        ?? item.data
        ?? JSON.stringify(item);

    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);

    return value.toString();
}

const formatList = (items, formatter = formatObjectValue) => {
    if (!Array.isArray(items)) return "";

    return items
        .map(formatter)
        .filter(Boolean)
        .join(" | ");
}

const formatPhones = (phones) => {
    return formatList(phones, phone => {
        const type = phone?.type ? `${phone.type}: ` : "";
        return phone?.data ? `${type}${phone.data}` : "";
    });
}

const formatAddresses = (addresses) => {
    return formatList(addresses, address => {
        const location = [
            address?.address,
            address?.number,
            address?.neighborhood,
            address?.city
        ].filter(Boolean).join(", ");

        if (!address?.type) return location;
        if (!location) return address.type;

        return `${address.type}: ${location}`;
    });
}

const getIndividualJsonData = (row) => {
    if (!row?.data) return row ?? {};
    const data = row.data;

    const cached = individualJsonCache.get(row);
    if (cached?.data === data) return cached.value;

    try {
        const value = JSON.parse(data);
        individualJsonCache.set(row, {data, value});

        return value;
    } catch {
        return {};
    }
}

const getJsonValue = (row, field) => getIndividualJsonData(row)?.[field];

const JsonDataRenderer = ({value, valueFormatted}) => {
    const data = valueFormatted ?? value;

    if (data === null || data === undefined) return "";
    if (typeof data === "boolean") return formatBoolean(data);
    if (Array.isArray(data)) return formatList(data);
    if (typeof data === "object") return formatObjectValue(data);

    return data;
}

const jsonColumn = (column, hidden = false) => ({
    ...column,
    hide: column.hide ?? hidden,
    valueGetter: column.valueGetter ?? (({data}) => getJsonValue(data, column.field)),
    cellRenderer: JsonDataRenderer,
    comparator: column.comparator ?? aggridUtils.nullLastComparator,
});

const IndividualPhotoGrid = ({data: {data}}) => {
    return (
        <div className={"h-full p-0.5"}>
            <IndividualPhoto individual={JSON.parse(data)} fullHeight={true} fromGrid={true}/>
        </div>
    );
}

const getInitialColumnDefs = () => {
    const columns = [
        {headerName: "", field: "", width: 50, sortable: false, cellRenderer: IndividualPhotoGrid},
        {headerName: "Nome", field: "name", flex: 1, minWidth: 220, sort: "asc", comparator: aggridUtils.nullLastComparator},
        {headerName: "CPF", field: "cpf", width: 120, valueFormatter: ({value}) => projectUtils.formatCpfToView(value), comparator: aggridUtils.nullLastComparator},
        {headerName: "Associações", field: "associations", width: 110, sortable: false, cellRenderer: AssociationRenderer},
        jsonColumn({headerName: "RG", field: "rg", width: 100}),
        jsonColumn({headerName: "Apelido", field: "nickname", width: 150}),
        jsonColumn({headerName: "Nome social", field: "socialName", width: 180}),
        jsonColumn({headerName: "Sexo", field: "sex", width: 100}),
        jsonColumn({headerName: "Nascimento", field: "dtBirth", width: 100, valueFormatter: ({value}) => projectUtils.formatDate(value, "DD/MM/YYYY"), comparator: aggridUtils.agGridSimpleDateComparator}),
        jsonColumn({headerName: "Naturalidade", field: "cityOfBirth", width: 190}),
        jsonColumn({headerName: "Nome da mãe", field: "mother", width: 220}),
        jsonColumn({headerName: "Nome do pai", field: "father", width: 220}),
        jsonColumn({headerName: "Telefones", field: "phones", width: 210, valueGetter: ({data}) => formatPhones(getJsonValue(data, "phones"))}),
        // jsonColumn({headerName: "Endereços", field: "addresses", width: 300, valueGetter: ({data}) => formatAddresses(getJsonValue(data, "addresses"))}),
        jsonColumn({headerName: "Logradouro", field: "address", width: 220, valueGetter: ({data}) => formatList(getJsonValue(data, "addresses"), address => address?.address)}, true),
        jsonColumn({headerName: "Bairro", field: "neighborhood", width: 180, valueGetter: ({data}) => formatList(getJsonValue(data, "addresses"), address => address?.neighborhood)}, true),
        jsonColumn({headerName: "Município", field: "city", width: 180, valueGetter: ({data}) => formatList(getJsonValue(data, "addresses"), address => address?.city)}, true),
        jsonColumn({headerName: "Complemento", field: "complement", width: 180, valueGetter: ({data}) => formatList(getJsonValue(data, "addresses"), address => address?.complement)}, true),
        jsonColumn({headerName: "CEP", field: "zipCode", width: 110, valueGetter: ({data}) => formatList(getJsonValue(data, "addresses"), address => address?.zipCode ?? address?.zipcode)}, true),
        jsonColumn({headerName: "Situação", field: "situation", width: 140}),
        jsonColumn({headerName: "Situação de rua", field: "homeless", width: 130, valueFormatter: ({value}) => formatBoolean(value), comparator: booleanComparator}),
        jsonColumn({headerName: "Transtorno mental", field: "mentalDisorder", width: 150, valueFormatter: ({value}) => formatBoolean(value), comparator: booleanComparator}),
        jsonColumn({headerName: "CNH", field: "cnh", width: 120}, true),
        jsonColumn({headerName: "Emissor RG", field: "emitterRg", width: 120}, true),
        jsonColumn({headerName: "RG criminal", field: "criminalRg", width: 130}, true),
        jsonColumn({headerName: "Nacionalidade", field: "nationality", width: 140}, true),
        jsonColumn({headerName: "Estado civil", field: "maritalStatus", width: 130}, true),
        jsonColumn({headerName: "Grau de instrução", field: "educationDegree", width: 150}, true),
        jsonColumn({headerName: "Profissão", field: "occupation", width: 150}, true),
        jsonColumn({headerName: "Função no crime", field: "roleCrime", width: 150}, true),
        jsonColumn({headerName: "Altura", field: "height", width: 100}, true),
        jsonColumn({headerName: "Cútis", field: "color", width: 120}, true),
        jsonColumn({headerName: "Cor dos olhos", field: "eyes", width: 120}, true),
        jsonColumn({headerName: "Tipo dos cabelos", field: "hairType", width: 150}, true),
        jsonColumn({headerName: "Cor do cabelo", field: "hairColor", width: 150}, true),
        jsonColumn({headerName: "Comprimento dos cabelos", field: "hairSize", width: 170}, true),
        jsonColumn({headerName: "Compleição", field: "bodyShape", width: 140}, true),
        jsonColumn({headerName: "Local", field: "local", width: 150, valueGetter: ({data}) => formatList(getJsonValue(data, "individualRegionWrapper"), characteristic => characteristic?.local)}, true),
        jsonColumn({headerName: "Tipo", field: "type", width: 150, valueGetter: ({data}) => formatList(getJsonValue(data, "individualRegionWrapper"), characteristic => characteristic?.type)}, true),
        jsonColumn({headerName: "Descrição", field: "description", width: 240, valueGetter: ({data}) => formatList(getJsonValue(data, "individualRegionWrapper"), characteristic => characteristic?.description)}, true),
        jsonColumn({headerName: "Rede social", field: "socialNetwork", width: 170}, true),
        jsonColumn({headerName: "Informações do indivíduo", field: "information", width: 220}, true),
        jsonColumn({headerName: "Artigos indiciados", field: "article", width: 220, valueGetter: ({data}) => formatList(getJsonValue(data, "articles"), article => article?.article)}, true),
    ];

    const hiddenColumns = getStorageJson(HIDDEN_COLUMNS_STORAGE);
    const orderColumns = getStorageJson(ORDER_COLUMNS_STORAGE);

    if (hiddenColumns) columns.forEach(column => hiddenColumns.includes(column.field) ? column.hide = true : "");
    if (orderColumns) aggridUtils.sortColumns(columns, orderColumns);

    return columns;
}

const IndividualGrid = ({
    rowData = [],
    currentPage: controlledCurrentPage,
    pageSize: controlledPageSize,
    onCurrentPageChange,
    onPageSizeChange,
}) => {
    const navigate = navigateHook();
    const [columnDefs, setColumnDefs] = useState(getInitialColumnDefs);
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const [internalPageSize, setInternalPageSize] = useState(() => getIndividualDefaultPageSize("grid"));

    const currentPage = controlledCurrentPage ?? internalCurrentPage;
    const pageSize = controlledPageSize ?? internalPageSize;
    const setCurrentPage = onCurrentPageChange ?? setInternalCurrentPage;
    const setPageSize = onPageSizeChange ?? setInternalPageSize;

    const paginatedRowData = useMemo(() => (
        getIndividualPaginatedRowData(rowData, currentPage, pageSize)
    ), [currentPage, pageSize, rowData]);

    const toggleColumn = useCallback((field) => {
        setColumnDefs(prevDefs =>
            prevDefs.map(column => column.field === field ? {...column, hide: !column.hide} : column)
        );
    }, []);

    const onColumnMoved = useCallback((params) => {
        const columns = params.api.getAllDisplayedColumns().map(column => column.colId);
        localStorage.setItem(ORDER_COLUMNS_STORAGE, JSON.stringify(columns));
    }, []);

    const onColumnVisible = useCallback(({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    }, [toggleColumn]);

    const onRowClicked = ({data}) => {
        const cpf = data.cpf.replace("/", "-");

        const urlDev = `/individuals/form/${cpf}`
        
        navigate(urlDev, true)
    }

    return (
        <>
            <div className={"w-full flex justify-end gap-3"}>
                <ShowHideColumnsGrid
                    columnDefs={columnDefs}
                    toggleColumn={toggleColumn}
                    storageItem={HIDDEN_COLUMNS_STORAGE}
                    wrapperClassName={"relative flex items-center"}
                    buttonClassName={"relative! left-0! bottom-0! top-0! cursor-pointer items-center justify-center rounded text-gray-700 hover:bg-gray-50"}
                    menuClassName={"absolute right-0 top-9 z-999 h-[calc(100vh-176px)] w-[256px] overflow-y-scroll rounded-md border border-gray-300 bg-white shadow"}
                />

                <IndividualGridPagination
                    rowData={rowData}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    modeVisualization="grid"
                    onCurrentPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                />
            </div>

            <div className={"aggrid-row-pointer h-[calc(100vh-192px)]"}>
                <AgGridReact
                    rowData={paginatedRowData}
                    columnDefs={columnDefs}
                    defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                    onColumnMoved={onColumnMoved}
                    onColumnVisible={onColumnVisible}
                    animateRows={false}
                    suppressCellFocus={true}
                    gridOptions={{localeText: aggridUtils.localization()}}
                    theme={aggridUtils.generalTheme({rowHeight: 42})}
                    onRowClicked={onRowClicked}
                />
            </div>
        </>
    );
};

export default IndividualGrid;
